const cfg = window.BB || {};
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Money ---------- */
export function formatMoney(cents, format) {
  const fmt = format || cfg.moneyFormat;
  const n = Number(cents) / 100;
  if (!fmt || (cfg.currency && cfg.shopCurrency && cfg.currency !== cfg.shopCurrency)) {
    try {
      return new Intl.NumberFormat(cfg.locale || 'en', { style: 'currency', currency: cfg.currency || 'USD' }).format(n);
    } catch (e) {
      return n.toFixed(2);
    }
  }
  const group = (value, decimals, thousands, dec) => {
    const [i, d] = value.toFixed(decimals).split('.');
    return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (d ? dec + d : '');
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
    switch (key) {
      case 'amount_no_decimals': return group(n, 0, ',', '.');
      case 'amount_with_comma_separator': return group(n, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return group(n, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return group(n, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator': return group(n, 0, ' ', ',');
      case 'amount_with_space_separator': return group(n, 2, ' ', ',');
      case 'amount_with_period_and_space_separator': return group(n, 2, ' ', '.');
      default: return group(n, 2, ',', '.');
    }
  });
}

/* ---------- Drawers (native <dialog>: focus containment + Escape) ---------- */
const openers = new WeakMap();
export function openDrawer(dialog, opener) {
  if (!dialog || dialog.open) return;
  if (opener) {
    openers.set(dialog, opener);
    opener.setAttribute('aria-expanded', 'true');
  }
  dialog.showModal();
  document.documentElement.classList.add('is-locked');
  requestAnimationFrame(() => dialog.classList.add('is-open'));
  const focusTarget = dialog.querySelector('[autofocus], [data-drawer-focus]');
  if (focusTarget) focusTarget.focus();
}
export function closeDrawer(dialog) {
  if (!dialog || !dialog.open) return;
  dialog.classList.remove('is-open');
  const done = () => {
    dialog.close();
    if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('is-locked');
    const opener = openers.get(dialog);
    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
      opener.focus();
    }
  };
  if (reducedMotion.matches) done();
  else setTimeout(done, 280);
}
document.addEventListener('click', (e) => {
  const openBtn = e.target.closest('[data-drawer-open]');
  if (openBtn) {
    const dialog = document.getElementById(openBtn.getAttribute('data-drawer-open'));
    if (dialog) {
      e.preventDefault();
      openDrawer(dialog, openBtn);
    }
    return;
  }
  const closeBtn = e.target.closest('[data-drawer-close]');
  if (closeBtn) {
    closeDrawer(closeBtn.closest('dialog'));
    return;
  }
  if (e.target.matches('dialog.drawer')) closeDrawer(e.target);
});
document.addEventListener('cancel', (e) => {
  if (e.target.matches('dialog.drawer')) {
    e.preventDefault();
    closeDrawer(e.target);
  }
}, true);

/* ---------- Media slot: autoplay in view, pause out of view, facades ---------- */
const videoObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
  entries.forEach(({ target, isIntersecting }) => {
    if (isIntersecting && !reducedMotion.matches) {
      const p = target.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      target.pause();
    }
  });
}, { threshold: 0.25 }) : null;
export function initMedia(root = document) {
  root.querySelectorAll('video[data-ms-autoplay]').forEach((v) => {
    if (v.dataset.msBound) return;
    v.dataset.msBound = '1';
    v.muted = true;
    if (videoObserver) videoObserver.observe(v);
  });
}
document.addEventListener('click', (e) => {
  const play = e.target.closest('.ms__play');
  if (!play) return;
  const facade = play.closest('[data-ms-embed]');
  if (!facade) return;
  const id = encodeURIComponent(facade.dataset.msId);
  const muted = facade.hasAttribute('data-ms-muted') ? 1 : 0;
  const loop = facade.hasAttribute('data-ms-loop') ? 1 : 0;
  let src = '';
  if (facade.dataset.msEmbed === 'youtube') {
    src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&playsinline=1&rel=0&mute=${muted}${loop ? `&loop=1&playlist=${id}` : ''}`;
  } else if (facade.dataset.msEmbed === 'vimeo') {
    src = `https://player.vimeo.com/video/${id}?autoplay=1&muted=${muted}&loop=${loop}&dnt=1`;
  }
  if (!src) return;
  const iframe = document.createElement('iframe');
  iframe.src = src;
  iframe.title = facade.dataset.msTitle || '';
  iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
  iframe.allowFullscreen = true;
  facade.replaceChildren(iframe);
  iframe.focus();
});

/* ---------- Announcement rotation ---------- */
function initAnnouncements(root = document) {
  root.querySelectorAll('[data-ann-rotate]').forEach((bar) => {
    if (bar.dataset.bound) return;
    bar.dataset.bound = '1';
    const msgs = [...bar.querySelectorAll('.ann__msg')];
    if (msgs.length < 2) return;
    let i = 0;
    let timer;
    const show = (n) => {
      msgs[i].classList.remove('is-active');
      msgs[i].setAttribute('aria-hidden', 'true');
      i = (n + msgs.length) % msgs.length;
      msgs[i].classList.add('is-active');
      msgs[i].removeAttribute('aria-hidden');
    };
    const start = () => {
      if (reducedMotion.matches) return;
      clearInterval(timer);
      timer = setInterval(() => show(i + 1), Number(bar.dataset.annRotate) * 1000);
    };
    bar.querySelector('.ann__nav--prev')?.addEventListener('click', () => { show(i - 1); start(); });
    bar.querySelector('.ann__nav--next')?.addEventListener('click', () => { show(i + 1); start(); });
    bar.addEventListener('mouseenter', () => clearInterval(timer));
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', () => clearInterval(timer));
    bar.addEventListener('focusout', start);
    start();
  });
}

/* ---------- Header scroll state ---------- */
function initHeader() {
  const hdr = document.querySelector('[data-header]');
  if (!hdr) return;
  const f = () => hdr.classList.toggle('is-scrolled', window.scrollY > 8);
  f();
  window.addEventListener('scroll', f, { passive: true });
}

/* ---------- Cart ---------- */
const routes = cfg.routes || {};
const cartSectionIds = () => [...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection);

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  if (!res.ok) {
    const msg = (data && (data.description || data.message)) || cfg.strings?.cartError || 'Error';
    const err = new Error(typeof msg === 'string' ? msg : cfg.strings?.cartError);
    err.status = res.status;
    throw err;
  }
  return data;
}

function applySections(sections) {
  if (!sections) return;
  Object.entries(sections).forEach(([id, htmlStr]) => {
    if (!htmlStr) return;
    const doc = new DOMParser().parseFromString(htmlStr, 'text/html');
    const fresh = doc.querySelector('[data-cd-content]');
    const live = document.querySelector(`[data-cart-section="${id}"] [data-cd-content]`);
    if (fresh && live) {
      live.replaceWith(fresh);
      initMedia(fresh);
    }
  });
}

function setCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.dataset.count = count;
  });
  document.querySelectorAll('[data-cart-link]').forEach((el) => {
    el.setAttribute('aria-label', (cfg.strings?.cartCount || '').replace('[count]', count));
  });
}

async function refreshCart(sections) {
  const ids = cartSectionIds();
  const tasks = [fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json())];
  if (!sections && ids.length) {
    tasks.push(fetch(`${window.location.pathname}?sections=${ids.join(',')}`).then((r) => r.json()));
  }
  const [cart, fetched] = await Promise.all(tasks);
  applySections(sections || fetched);
  setCount(cart.item_count);
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  return cart;
}

export const Cart = {
  async add(items, opener) {
    const ids = cartSectionIds();
    const body = { items };
    if (ids.length) {
      body.sections = ids;
      body.sections_url = window.location.pathname;
    }
    const res = await postJSON(`${routes.cartAdd}.js`, body);
    await refreshCart(res.sections);
    const drawer = document.querySelector('[data-cart-drawer]');
    if (drawer) openDrawer(drawer, opener || document.querySelector('[data-cart-link]'));
    else window.location.href = routes.cart;
    return res;
  },
  async change(line, quantity) {
    const ids = cartSectionIds();
    const res = await postJSON(`${routes.cartChange}.js`, { line, quantity, sections: ids, sections_url: window.location.pathname });
    applySections(res.sections);
    setCount(res.item_count);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: res } }));
    return res;
  },
  async update(payload) {
    const ids = cartSectionIds();
    const res = await postJSON(`${routes.cartUpdate}.js`, Object.assign({ sections: ids, sections_url: window.location.pathname }, payload));
    applySections(res.sections);
    setCount(res.item_count);
    return res;
  },
  refresh: refreshCart
};
window.BBCart = Cart;

function cartError(msg) {
  const el = document.querySelector('[data-cart-drawer] [data-cd-error]');
  if (el) el.textContent = msg;
}

document.addEventListener('click', async (e) => {
  const step = e.target.closest('[data-cd-step]');
  const remove = e.target.closest('[data-cd-remove]');
  const codeRemove = e.target.closest('[data-cd-code-remove]');
  const codeApply = e.target.closest('[data-cd-code-apply]');
  const cartLink = e.target.closest('[data-cart-link]');
  try {
    if (cartLink && document.querySelector('[data-cart-drawer]')) {
      e.preventDefault();
      openDrawer(document.querySelector('[data-cart-drawer]'), cartLink);
    } else if (step) {
      const input = step.closest('.qty').querySelector('input');
      const next = Math.max(0, Number(input.value) + Number(step.dataset.cdStep));
      step.closest('.cd__item')?.setAttribute('aria-busy', 'true');
      await Cart.change(Number(input.dataset.line), next);
    } else if (remove) {
      await Cart.change(Number(remove.dataset.cdRemove), 0);
    } else if (codeApply) {
      const input = document.querySelector('[data-cd-code-input]');
      const code = input.value.trim();
      if (!code) return;
      const existing = [...document.querySelectorAll('[data-cd-code-remove]')].map((b) => b.dataset.cdCodeRemove);
      const res = await Cart.update({ discount: [...existing, code].join(',') });
      const applied = (res.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      if (!applied || !applied.applicable) cartError(cfg.strings?.discountInvalid || '');
    } else if (codeRemove) {
      const keep = [...document.querySelectorAll('[data-cd-code-remove]')].map((b) => b.dataset.cdCodeRemove).filter((c) => c !== codeRemove.dataset.cdCodeRemove);
      await Cart.update({ discount: keep.join(',') });
    }
  } catch (err) {
    cartError(err.message);
  }
});

document.addEventListener('change', async (e) => {
  const qty = e.target.closest('[data-cd-qty]');
  try {
    if (qty) await Cart.change(Number(qty.dataset.line), Math.max(0, Number(qty.value)));
    if (e.target.matches('[data-cd-note]')) await Cart.update({ note: e.target.value });
  } catch (err) {
    cartError(err.message);
  }
});

document.addEventListener('submit', async (e) => {
  const form = e.target.closest('[data-cd-upsell]');
  if (!form) return;
  e.preventDefault();
  const btn = form.querySelector('button');
  btn.classList.add('is-loading');
  try {
    await Cart.add([{ id: Number(form.querySelector('[name="id"]').value), quantity: 1 }]);
  } catch (err) {
    cartError(err.message);
  } finally {
    btn.classList.remove('is-loading');
  }
});

/* ---------- Boot + theme editor hooks ---------- */
function boot(root = document) {
  initMedia(root);
  initAnnouncements(root);
}
boot();
initHeader();
document.addEventListener('shopify:section:load', (e) => boot(e.target));
document.addEventListener('shopify:section:select', (e) => {
  const d = e.target.querySelector('dialog.drawer');
  if (d && e.target.querySelector('[data-cart-drawer]')) openDrawer(d);
});
document.addEventListener('shopify:section:deselect', (e) => {
  const d = e.target.querySelector('dialog.drawer[open]');
  if (d) closeDrawer(d);
});
